<template>
  <div>
    <div class="row m-b-20">
      <div class="col-xs-12 tw-flex">
        <div class="tw-max-w-[250px]">
          <WdsInputDate v-model="dateRange.data" format="yyyy-mm-dd" />
        </div>
        <WdsSelect
          v-if="divisions.length > 0 && filterIsEnabled('divisions')"
          v-model="division.data"
          class="m-l-10"
          :list="divisions"
          :placeholder="$t('Divisions')"
          multi-select
          searchable
        >
          <template #select-all-text>
            <WdsText :text="$t('All divisions')" />
          </template>
        </WdsSelect>
        <WdsSelect
          v-if="teamMembers.length > 0 && filterIsEnabled('teamMembers')"
          v-model="teamMember.data"
          class="m-l-10"
          :list="teamMembers"
          :placeholder="$t('Team members')"
          multi-select
          searchable
        >
          <template #select-all-text>
            <WdsText :text="$t('All job owners')" />
          </template>
        </WdsSelect>
        <WdsSelect
          v-if="filterIsEnabled('sources')"
          v-model="Sources"
          class="m-l-10"
          :list="sourceOptions"
          :placeholder="$t('Sources')"
          multi-select
          searchable
        >
          <template #select-all-text>
            <WdsText :text="$t('All sources')" />
          </template>
        </WdsSelect>
        <a href="#" class="tw-inline-block m-l-15 f-12" @click="resetFilter">
          {{ $t('Reset filters') }}
          <i :class="{ loading: isResetting }" class="fa fa-undo" />
        </a>
      </div>
    </div>
    <slot :filter="Filter" :date-range="DateRange" />
  </div>
</template>

<script>
import { WdsInputDate, WdsSelect, WdsText } from '@worksome/wds-components'
import DateRange from '@/vue-prop-types/DateRange'
import { LocalStorageEncoder } from '@/utils/Encoders'

import MultiSelectDecodeBuilderMixin from '@/mixins/MultiSelectDecodeBuilderMixin'

const SourceOptions = [
  {
    label: $t('Marketplace'),
    value: 'market',
  },
  {
    label: $t('Trusted Contacts'),
    value: 'trustedContacts',
  },
  {
    label: $t('Recruiters'),
    value: 'recruiter',
  },
]

export default {
  name: 'DataVisGlobalFilter',
  components: {
    WdsText,
    WdsInputDate,
    WdsSelect,
  },
  mixins: [MultiSelectDecodeBuilderMixin],
  props: {
    divisions: {
      type: Object,
      default: () => {},
    },
    teamMembers: {
      type: Object,
      default: () => {},
    },
    categories: {
      type: Object,
      default: () => {},
    },
    filters: {
      type: Array,
      default: () => ['divisions', 'teamMembers', 'categories', 'sources'],
    },
  },
  data() {
    const propDivisions = this.divisions
    return {
      sourceOptions: SourceOptions,
      dateRange: new LocalStorageEncoder({
        storageKey: 'data_vis_created_YYYY-MM-DD',
        queryPoint: 'created',
        decoder: (dateStr) => DateRange.fromString({ str: dateStr, format: 'YYYY-MM-DD' }),
        encoder: (date) => `${date.start},${date.end}`,
        defaultVal: new DateRange({ format: 'YYYY-MM-DD' }),
      }),
      sources: new LocalStorageEncoder({
        storageKey: 'data_vis_sources',
        queryPoint: 'sources',
        decoder: (sourceStr, { _default }) => {
          const vals = {}
          sourceStr
            .split(',')
            .filter((v) => v !== '')
            .forEach((k) => {
              vals[k] = true
            })

          // We can now quickly find our option from our id
          return { ..._default, ...vals }
        },
        defaultVal: {
          market: false,
          recruiter: false,
          trustedContacts: false,
        },
      }),
      division: new LocalStorageEncoder({
        storageKey: 'data_vis_division',
        queryPoint: 'division',
        decoder: (idStr) =>
          idStr
            .split(',')
            .map((id) => propDivisions.find((div) => div.value === Number(id)))
            .filter((v) => !!v),
        encoder: (data) => data.map(({ value }) => value),
        defaultVal: [],
      }),
      teamMember: new LocalStorageEncoder({
        storageKey: 'data_vis_team',
        queryPoint: 'team',
        decoder: (idStr) =>
          idStr
            .split(',')
            .map((id) => propDivisions.find((div) => div.value === Number(id)))
            .filter((v) => !!v),
        encoder: (data) => data.map(({ value }) => value),
        defaultVal: [],
      }),
      selectedCategories: new LocalStorageEncoder({
        storageKey: 'data_vis_cats',
        queryPoint: 'cats',
        decoder: this.categoryDecoder(this.categories),
        filter: (categories) => categories.filter((c) => c.parentID === 0),
        defaultVal: [],
      }),
      isResetting: false,
    }
  },
  computed: {
    Filter() {
      return {
        filter: {
          categories: this.FilterCategories,
          divisions: this.FilterDivisions,
          teamMembers: this.FilterTeam,
          sources: this.FilterSource,
        },
        dateFrom: this.dateRange.data.start,
        dateTo: this.dateRange.data.end,
        currency: null, // Not used
      }
    },
    FilterCategories() {
      if (this.selectedCategories.data.length === this.categories.length) {
        return null
      }
      return this.selectedCategories.data.map((c) => c.value)
    },
    FilterDivisions() {
      if (this.division.data.length === this.divisions.length) {
        return null
      }
      return this.division.data.map((d) => d.value)
    },
    FilterTeam() {
      if (this.teamMember.data.length === this.teamMembers.length) {
        return null
      }
      return this.teamMember.data.map((d) => d.value)
    },
    Categories() {
      return this.categories.filter((c) => c.parentID === 0)
    },
    SelectedCategories: {
      get() {
        return this.selectedCategories.data.filter((c) => c.parentID === 0)
      },
      set(arr) {
        const pIds = arr.map((c) => c.value)

        // Here we filter out any child category whose parent was not selected
        const childCategories = this.categories.filter((c) => pIds.includes(c.parentID))

        this.selectedCategories.data = [...arr, ...childCategories]
      },
    },
    FilterSource() {
      const allFalse = Object.values(this.sources.data).reduce((c, a) => c + a, 0) === 0
      return {
        trustedContacts: this.sources.data.trustedContacts || allFalse,
        marketplace: this.sources.data.market || allFalse,
        recruiters: this.sources.data.recruiter || allFalse,
      }
    },
    Sources: {
      get() {
        return SourceOptions.filter((source) => this.sources.data[source.value])
      },
      set(optionsSelected) {
        const nSources = {
          market: false,
          recruiter: false,
          trustedContacts: false,
        }
        // I know that this is O(n^2) but we only have 3 options so this should not be an issue
        SourceOptions.forEach((option) => {
          nSources[option.value] =
            optionsSelected.find((selected) => selected.value === option.value) !== undefined
        })

        this.sources.data = nSources
      },
    },
    DateRange() {
      return DateRange.copy(this.dateRange.data)
    },
  },
  methods: {
    categoryDecoder(options) {
      const decoder = this.multiSelectDecodeBuilder(options)

      return (idStr, obj) => {
        const res = decoder(idStr, obj)
        const pIds = res.map((c) => c.value)
        const childCategories = options.filter((c) => pIds.includes(c.parentID))
        return [...res, ...childCategories]
      }
    },
    resetFilter() {
      this.isResetting = true
      setTimeout(() => {
        this.isResetting = false
      }, 600)
      this.SelectedCategories = [] // this.Categories
      this.sources.data = {
        market: false,
        recruiter: false,
        trustedContacts: false,
      }
      this.division.data = [] // this.divisions
      this.teamMember.data = [] // this.teamMembers
      this.dateRange.data = new DateRange({ format: 'YYYY-MM-DD' })
    },
    filterIsEnabled(filter) {
      const { length } = this.filters
      for (let i = 0; i < length; i++) {
        if (this.filters[i] === filter) return true
      }
      return false
    },
  },
}
</script>

<style scoped lang="scss">
@-webkit-keyframes rotation {
  from {
    -webkit-transform: rotate(359deg);
  }
  to {
    -webkit-transform: rotate(0deg);
  }
}

.fa-undo.loading {
  -webkit-animation: rotation 0.3s 1 ease-out;
}
</style>
